// Multilingual Text Support
export interface MultilingualText {
  [languageCode: string]: string;
}

// Field Types
export type FieldType =
  | 'text'
  | 'email'
  | 'number'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'datetime'
  | 'file'
  | 'richtext'
  | 'signature'
  | 'repeatable'
  | 'section'      // Section with title and description
  | 'divider'      // Horizontal divider
  | 'page_break'   // Multi-page separator
  | 'layout'       // Multi-column layout container
  | 'phone'        // Phone with country code
  | 'rating'       // Star rating
  | 'slider'       // Range slider
  | 'url'          // URL input
  | 'color'        // Color picker
  | 'hidden'       // Hidden field
  | 'matrix'       // Matrix/Grid questions
  | 'ranking'      // Drag to rank items
  | 'calculated'   // Calculated field (formulas)
  | 'address'      // Address with autocomplete
  | 'payment'      // Payment field (Stripe/PayPal)
  | 'appointment'  // Date/time booking
  | 'nps'          // Net Promoter Score (0-10)
  | 'country'      // Country/State cascading selector
  | 'image_choice' // Select options with images
  | 'opinion_scale'; // Likert scale questions

// Validation Types
export type ValidationOperator =
  | 'required'
  | 'minLength'
  | 'maxLength'
  | 'min'
  | 'max'
  | 'pattern'
  | 'email'
  | 'url'
  | 'phone';

export interface ValidationRule {
  type: ValidationOperator;
  value?: string | number;
  message: string;
}

// Select/Radio/Checkbox Options
export interface FieldOption {
  label: string;
  value: string;
  // Multilingual labels for each language
  labels?: MultilingualText;
}

// Custom validation messages
export interface ValidationMessages {
  required?: string;
  minLength?: string;
  maxLength?: string;
  min?: string;
  max?: string;
  pattern?: string;
  email?: string;
  url?: string;
}

// Matrix question row/column
export interface MatrixRow {
  id: string;
  label: string;
}

export interface MatrixColumn {
  id: string;
  label: string;
  value: string;
}

// Ranking item
export interface RankingItem {
  id: string;
  label: string;
}

// Image choice option
export interface ImageChoiceOption {
  id: string;
  label: string;
  value: string;
  imageUrl: string;
  description?: string;
}

// Appointment time slot
export interface AppointmentTimeSlot {
  start: string;  // HH:mm format
  end: string;    // HH:mm format
}

// Appointment availability
export interface AppointmentAvailability {
  dayOfWeek: number;  // 0-6 (Sunday-Saturday)
  slots: AppointmentTimeSlot[];
}

// Opinion scale label
export interface OpinionScaleLabel {
  value: number;
  label: string;
}

// Field Properties (type-specific settings)
export interface FieldProperties {
  // Text fields
  minLength?: number;
  maxLength?: number;
  pattern?: string;

  // Number fields
  min?: number;
  max?: number;
  step?: number;

  // Select fields
  multiple?: boolean;

  // File fields
  accept?: string;
  maxSize?: number; // in bytes
  maxFiles?: number;

  // Repeatable sections
  minItems?: number;
  maxItems?: number;
  addButtonText?: string;
  fields?: FormField[]; // DEPRECATED: Use columnFields instead (kept for backward compatibility)

  // Layout container & Repeatable sections (both use column-based structure)
  columns?: number;           // Number of columns (1-4) - Default 1 for repeatable
  columnFields?: FormField[][]; // Array of field arrays, one per column - Used by both layout and repeatable
  columnWidths?: number[];    // Width percentages for each column (sum = 100)
  columnGap?: 'small' | 'medium' | 'large'; // Gap between columns
  verticalAlign?: 'top' | 'center' | 'bottom'; // Vertical alignment of columns

  // Date fields
  minDate?: string;
  maxDate?: string;

  // Section/Page fields
  sectionTitle?: string;
  sectionDescription?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;

  // Divider
  dividerStyle?: 'solid' | 'dashed' | 'dotted';
  dividerColor?: string;

  // Rich Text Content
  content?: string;  // HTML content for richtext field

  // Page break
  pageTitle?: string;
  pageDescription?: string;

  // Phone field
  countryCode?: string;       // Default country code (e.g., 'US', 'IL')
  phoneFormat?: 'international' | 'national';

  // Rating field
  ratingMax?: number;         // Max stars (default 5)
  ratingIcon?: 'star' | 'heart' | 'circle';
  allowHalf?: boolean;        // Allow half ratings

  // Slider field
  sliderMin?: number;
  sliderMax?: number;
  sliderStep?: number;
  showValue?: boolean;        // Show current value
  showMinMax?: boolean;       // Show min/max labels

  // Color field
  colorFormat?: 'hex' | 'rgb' | 'hsl';
  colorPresets?: string[];    // Preset color options

  // Hidden field
  hiddenValue?: string;       // The hidden value to submit

  // Matrix field
  matrixRows?: MatrixRow[];
  matrixColumns?: MatrixColumn[];
  matrixType?: 'single' | 'multiple';  // Single or multiple selection per row

  // Ranking field
  rankingItems?: RankingItem[];

  // Calculated field
  calculationType?: 'sum' | 'average' | 'min' | 'max' | 'count' | 'formula';
  calculationFields?: string[];     // Field IDs to calculate from
  calculationFormula?: string;      // Custom formula (e.g., "{field1} + {field2} * 2")
  calculationFormat?: 'number' | 'currency' | 'percent';
  calculationDecimals?: number;     // Number of decimal places
  calculationCurrency?: string;     // Currency symbol (e.g., "$", "€")
  calculationPrefix?: string;       // Prefix text
  calculationSuffix?: string;       // Suffix text

  // Quiz mode properties
  correctAnswer?: string | string[] | number | boolean;  // Correct answer(s)
  partialCredit?: boolean;          // Allow partial credit for multiple answers
  points?: number;                  // Points for this question (default 1)
  explanation?: string;             // Explanation shown after answering
  caseSensitive?: boolean;          // For text answers, case sensitive comparison

  // Address field
  addressComponents?: ('street' | 'street2' | 'city' | 'state' | 'zip' | 'country')[];
  googlePlacesApiKey?: string;      // Google Places API key for autocomplete
  defaultCountry?: string;          // Default country code (e.g., 'US')
  restrictCountries?: string[];     // Restrict to specific countries

  // Payment field
  paymentProvider?: 'stripe' | 'paypal';
  paymentAmount?: number;           // Fixed amount (if not dynamic)
  paymentCurrency?: string;         // Currency code (e.g., 'USD')
  paymentDescription?: string;      // Payment description
  stripePublicKey?: string;         // Stripe public key
  paypalClientId?: string;          // PayPal client ID
  paymentMode?: 'live' | 'test';    // Payment mode

  // Appointment field
  appointmentDuration?: number;     // Duration in minutes
  appointmentAvailability?: AppointmentAvailability[];
  appointmentMinAdvance?: number;   // Min hours in advance to book
  appointmentMaxAdvance?: number;   // Max days in advance to book
  appointmentTimezone?: string;     // Timezone for appointments
  blockedDates?: string[];          // Dates that are unavailable

  // NPS field
  npsLeftLabel?: string;            // Label for 0 (e.g., "Not likely")
  npsRightLabel?: string;           // Label for 10 (e.g., "Very likely")
  npsShowLabels?: boolean;          // Show labels under numbers

  // Country/State field
  countryFieldType?: 'country_only' | 'country_state' | 'country_state_city';
  defaultCountryCode?: string;      // Default selected country
  popularCountries?: string[];      // Countries to show at top of list

  // Image choice field
  imageChoices?: ImageChoiceOption[];
  imageChoiceColumns?: 2 | 3 | 4;   // Number of columns in grid
  imageChoiceMultiple?: boolean;    // Allow multiple selections
  imageChoiceShowLabels?: boolean;  // Show labels under images

  // Opinion scale field
  opinionScaleMin?: number;         // Minimum value (default 1)
  opinionScaleMax?: number;         // Maximum value (default 5)
  opinionScaleLabels?: OpinionScaleLabel[];  // Custom labels for values
  opinionScaleLeftLabel?: string;   // Left endpoint label
  opinionScaleRightLabel?: string;  // Right endpoint label
  opinionScaleShowNumbers?: boolean; // Show numbers on scale

  // Custom validation messages
  validationMessages?: ValidationMessages;
}

// Main Field Definition
export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  name: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  validation?: ValidationRule[];
  options?: FieldOption[];
  defaultValue?: unknown;
  properties: FieldProperties;
  conditionalId?: string;

  // Multilingual support - stores translations for each language
  labels?: MultilingualText;
  placeholders?: MultilingualText;
  helpTexts?: MultilingualText;
}

// Multi-step Form Step
export interface FormStep {
  id: string;
  title: string;
  description?: string;
  fieldIds: string[];

  // Multilingual support
  titles?: MultilingualText;
  descriptions?: MultilingualText;
}

// Conditional Logic
export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'is_empty'
  | 'is_not_empty';

export type ConditionalAction = 'show' | 'hide' | 'enable' | 'disable' | 'require' | 'skip_to_page';

export interface Condition {
  fieldId: string;
  operator: ConditionOperator;
  value: unknown;
}

export interface ConditionalRule {
  id: string;
  targetFieldId: string;           // Field, section, or page_break ID
  targetType?: 'field' | 'section' | 'page';  // What type of target
  action: ConditionalAction;
  conditions: Condition[];
  operator: 'and' | 'or';
  skipToPageId?: string;           // For skip_to_page action - which page to skip to
}

// Submission Action Types
export interface DatabaseAction {
  type: 'database';
}

export interface EmailAction {
  type: 'email';
  to: string;
  subject?: string;
  replyTo?: string;
}

export interface WebhookAction {
  type: 'webhook';
  url: string;
  method?: 'POST' | 'PUT';
  headers?: Record<string, string>;
}

export interface GoogleSheetsAction {
  type: 'google_sheets';
  spreadsheetId: string;
  sheetName?: string;
  credentials?: string; // Encrypted service account JSON
  fieldMapping?: Record<string, string>; // fieldId -> column letter/name
}

export interface SlackAction {
  type: 'slack';
  webhookUrl: string;
  channel?: string;
  username?: string;
  iconEmoji?: string;
  messageTemplate?: string;
}

export interface DiscordAction {
  type: 'discord';
  webhookUrl: string;
  username?: string;
  avatarUrl?: string;
  messageTemplate?: string;
  embedColor?: string;
}

export interface ZapierAction {
  type: 'zapier';
  webhookUrl: string;          // Zapier catch hook URL
  includeMetadata?: boolean;   // Include submission metadata
}

export interface HubSpotAction {
  type: 'hubspot';
  apiKey: string;              // HubSpot private app access token
  createContact?: boolean;     // Create/update contact
  createDeal?: boolean;        // Also create a deal
  emailFieldId?: string;       // Field containing email
  fieldMapping?: Record<string, string>;  // formFieldId -> HubSpot property
  dealPipeline?: string;       // Pipeline ID for deals
  dealStage?: string;          // Stage ID for deals
}

export interface SalesforceAction {
  type: 'salesforce';
  instanceUrl: string;         // Salesforce instance URL
  accessToken: string;         // OAuth access token
  refreshToken?: string;       // OAuth refresh token
  objectType: 'Lead' | 'Contact' | 'Custom';
  customObjectName?: string;   // For custom objects
  fieldMapping?: Record<string, string>;  // formFieldId -> Salesforce field
}

export interface MailchimpAction {
  type: 'mailchimp';
  apiKey: string;              // Mailchimp API key
  listId: string;              // Audience/List ID
  emailFieldId?: string;       // Field containing email
  mergeFieldMapping?: Record<string, string>;  // formFieldId -> merge tag
  tags?: string[];             // Tags to apply to subscriber
  doubleOptIn?: boolean;       // Require double opt-in
  updateExisting?: boolean;    // Update if subscriber exists
}

export interface NotionAction {
  type: 'notion';
  integrationToken: string;    // Notion integration token
  databaseId: string;          // Database ID to add to
  fieldMapping?: Record<string, string>;  // formFieldId -> Notion property
  titleFieldId?: string;       // Field to use as page title
}

export interface AirtableAction {
  type: 'airtable';
  apiKey: string;              // Airtable personal access token
  baseId: string;              // Airtable base ID
  tableId: string;             // Table ID or name
  fieldMapping?: Record<string, string>;  // formFieldId -> Airtable field
}

export type SubmitAction = DatabaseAction | EmailAction | WebhookAction | GoogleSheetsAction | SlackAction | DiscordAction | ZapierAction | HubSpotAction | SalesforceAction | MailchimpAction | NotionAction | AirtableAction;

// Theme Types
export interface CustomTheme {
  primary: string;
  background: string;
  text: string;
  border: string;
  error: string;
  success: string;
}

export type ThemeSetting = 'light' | 'dark' | CustomTheme;

// Auto-responder Settings for form submitters
export interface AutoResponderSettings {
  enabled: boolean;
  emailFieldId?: string;         // Which field contains the email to send to
  subject?: string;              // Email subject line
  message?: string;              // Email body content (supports {{field_name}} placeholders)
  fromName?: string;             // Display name for the sender
  replyTo?: string;              // Reply-to address
  includeSubmission?: boolean;   // Include submission data in email
}

// Save & Resume Settings
export interface SaveAndResumeSettings {
  enabled: boolean;
  autoSave?: boolean;            // Auto-save every N seconds
  autoSaveInterval?: number;     // Interval in seconds (default 30)
  expiryDays?: number;           // Days until saved progress expires (default 30)
  showSaveButton?: boolean;      // Show manual save button
  saveButtonText?: string;       // Text for save button
  resumePromptText?: string;     // Text shown when resuming
}

// Quiz Mode Type
export type QuizMode = 'simple' | 'live';

// Live Quiz Settings (Real-time interactive mode)
export interface LiveQuizSettingsConfig {
  // Timing
  questionTimeSeconds: number;           // Default 20 seconds
  showQuestionOnPlayerDevice: boolean;   // Or just show options

  // Scoring
  enableStreakBonus: boolean;            // Bonus for consecutive correct
  streakBonusMultiplier: number;         // e.g., 1.2x after 3 streak
  streakThreshold: number;               // How many correct to activate streak
  timeBasedScoring: boolean;             // Faster = more points
  maxPointsPerQuestion: number;          // e.g., 1000
  minPointsPerQuestion: number;          // Minimum points for correct (slow) answer

  // Display
  showLeaderboardAfterQuestion: boolean;
  leaderboardSize: number;               // Top N players
  showAnswerDistribution: boolean;       // Bar chart of answers
  showCorrectAnswer: boolean;            // Reveal correct answer after

  // Gameplay
  allowLateJoin: boolean;                // Join after game started
  autoAdvance: boolean;                  // Auto go to next question
  autoAdvanceDelaySeconds: number;       // Delay before auto-advance
  randomizeQuestions: boolean;           // Shuffle question order
  randomizeOptions: boolean;             // Shuffle answer options

  // Audio (optional)
  enableSounds: boolean;
  lobbyMusicUrl?: string;
  questionRevealSoundUrl?: string;
  correctAnswerSoundUrl?: string;
  wrongAnswerSoundUrl?: string;
  timerTickSoundUrl?: string;
}

// Quiz Mode Settings
export interface QuizSettings {
  enabled: boolean;
  mode?: QuizMode;                        // 'simple' (default) or 'live' (real-time interactive)
  showCorrectAnswers?: boolean;         // Show correct answers after submission
  showCorrectAnswersWhen?: 'immediately' | 'after_deadline' | 'never';
  showScore?: boolean;                  // Show score after submission
  showScorePercentage?: boolean;        // Show percentage in addition to points
  passingScore?: number;                // Minimum score to pass (percentage)
  passingMessage?: string;              // Message shown when passed
  failingMessage?: string;              // Message shown when failed
  showPointsPerQuestion?: boolean;      // Show points for each question
  randomizeQuestions?: boolean;         // Randomize question order
  randomizeOptions?: boolean;           // Randomize option order for choice fields
  timeLimitMinutes?: number;            // Optional time limit in minutes
  allowRetake?: boolean;                // Allow retaking the quiz
  maxAttempts?: number;                 // Maximum number of attempts (0 = unlimited)
  liveSettings?: LiveQuizSettingsConfig; // Settings for live mode
}

// Form Language Settings
export interface FormLanguageSettings {
  enabled: boolean;                    // Whether multilingual is enabled
  availableLanguages: string[];        // Language codes enabled for this form
  defaultLanguage: string;             // Default language code (e.g., 'en')
}

// ============================================================================
// INTEGRATIONS
// ============================================================================

// Supported integration providers
export type IntegrationProvider =
  // CRM
  | 'salesforce'
  | 'hubspot'
  | 'monday'
  | 'pipedrive'
  | 'zoho_crm'
  | 'dynamics365'
  | 'freshsales'
  // Automation
  | 'zapier'
  | 'power_automate'
  | 'make'           // formerly Integromat
  | 'n8n'
  | 'webhook'
  // Spreadsheets & Databases
  | 'google_sheets'
  | 'airtable'
  | 'notion'
  // Office 365 / Microsoft 365
  | 'excel_online'       // Excel Online (OneDrive/SharePoint)
  | 'onedrive'           // Save files to OneDrive
  | 'sharepoint'         // Save to SharePoint lists/libraries
  | 'outlook'            // Create events, send emails
  | 'onenote'            // Create notes
  | 'microsoft_todo'     // Create tasks
  | 'planner'            // Create Planner tasks
  // Communication
  | 'slack'
  | 'teams'
  | 'discord'
  // Email Marketing
  | 'mailchimp'
  | 'activecampaign'
  // Website Builders
  | 'wix';

// Integration status
export type IntegrationStatus = 'active' | 'inactive' | 'error' | 'pending_auth';

// Field mapping for integrations
export interface FieldMapping {
  formFieldId: string;           // ID of the form field
  formFieldName: string;         // Name of the form field (for display)
  targetField: string;           // Target field in the external system
  targetFieldLabel?: string;     // Label of the target field (for display)
  transform?: 'none' | 'uppercase' | 'lowercase' | 'trim' | 'date_iso' | 'number';
}

// Base integration configuration
export interface BaseIntegrationConfig {
  id: string;
  provider: IntegrationProvider;
  name: string;                  // User-defined name for this integration
  enabled: boolean;
  status: IntegrationStatus;
  fieldMappings: FieldMapping[];
  triggerOn: ('submission' | 'update' | 'delete')[];
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  lastError?: string;
}

// Webhook Integration (generic, Zapier, Power Automate, Make, n8n)
export interface WebhookIntegrationConfig extends BaseIntegrationConfig {
  provider: 'webhook' | 'zapier' | 'power_automate' | 'make' | 'n8n';
  webhookUrl: string;
  method: 'POST' | 'PUT';
  headers?: Record<string, string>;
  includeMetadata?: boolean;     // Include form metadata in payload
  retryOnFailure?: boolean;
  retryCount?: number;
  secretKey?: string;            // For webhook signature verification
}

// Salesforce Integration
export interface SalesforceIntegrationConfig extends BaseIntegrationConfig {
  provider: 'salesforce';
  instanceUrl: string;           // e.g., https://yourcompany.salesforce.com
  objectType: 'Lead' | 'Contact' | 'Account' | 'Opportunity' | 'Case' | 'Custom';
  customObjectName?: string;     // If objectType is 'Custom'
  accessToken?: string;          // Encrypted, stored securely
  refreshToken?: string;         // Encrypted, stored securely
  tokenExpiresAt?: string;
  assignmentRuleId?: string;     // Optional Salesforce assignment rule
  recordTypeId?: string;         // Optional record type
}

// HubSpot Integration
export interface HubSpotIntegrationConfig extends BaseIntegrationConfig {
  provider: 'hubspot';
  objectType: 'contact' | 'company' | 'deal' | 'ticket';
  portalId?: string;
  accessToken?: string;          // Encrypted
  refreshToken?: string;         // Encrypted
  tokenExpiresAt?: string;
  pipelineId?: string;           // For deals
  stageId?: string;              // For deals
  ownerId?: string;              // Assign to specific owner
}

// Monday.com Integration
export interface MondayIntegrationConfig extends BaseIntegrationConfig {
  provider: 'monday';
  boardId: string;
  groupId?: string;              // Optional, defaults to first group
  accessToken?: string;          // Encrypted
  columnMappings: FieldMapping[];
  createAsSubitem?: boolean;
  parentItemId?: string;         // If createAsSubitem is true
}

// Pipedrive Integration
export interface PipedriveIntegrationConfig extends BaseIntegrationConfig {
  provider: 'pipedrive';
  objectType: 'person' | 'organization' | 'deal' | 'lead';
  apiToken?: string;             // Encrypted
  pipelineId?: string;
  stageId?: string;
  ownerId?: string;
}

// Zoho CRM Integration
export interface ZohoCrmIntegrationConfig extends BaseIntegrationConfig {
  provider: 'zoho_crm';
  module: 'Leads' | 'Contacts' | 'Accounts' | 'Deals' | 'Custom';
  customModuleName?: string;
  accessToken?: string;          // Encrypted
  refreshToken?: string;         // Encrypted
  tokenExpiresAt?: string;
  dataCenterId: string;          // com, eu, in, com.cn, com.au
}

// Microsoft Dynamics 365 Integration
export interface Dynamics365IntegrationConfig extends BaseIntegrationConfig {
  provider: 'dynamics365';
  instanceUrl: string;           // e.g., https://yourorg.crm.dynamics.com
  entityName: 'lead' | 'contact' | 'account' | 'opportunity' | string;
  accessToken?: string;          // Encrypted
  refreshToken?: string;         // Encrypted
  tokenExpiresAt?: string;
}

// Google Sheets Integration
export interface GoogleSheetsIntegrationConfig extends BaseIntegrationConfig {
  provider: 'google_sheets';
  spreadsheetId: string;
  sheetName?: string;            // Defaults to first sheet
  headerRow?: number;            // Row containing column headers (default: 1)
  accessToken?: string;          // Encrypted
  refreshToken?: string;         // Encrypted
  tokenExpiresAt?: string;
  appendToEnd?: boolean;         // Append new rows at end (default: true)
}

// Airtable Integration
export interface AirtableIntegrationConfig extends BaseIntegrationConfig {
  provider: 'airtable';
  baseId: string;
  tableId: string;
  apiKey?: string;               // Encrypted (personal access token)
  viewId?: string;               // Optional view to use
}

// Notion Integration
export interface NotionIntegrationConfig extends BaseIntegrationConfig {
  provider: 'notion';
  databaseId: string;
  accessToken?: string;          // Encrypted
}

// Slack Integration
export interface SlackIntegrationConfig extends BaseIntegrationConfig {
  provider: 'slack';
  webhookUrl: string;
  channelId?: string;
  messageTemplate?: string;      // Custom message template with field placeholders
  includeAllFields?: boolean;
  notifyOnSubmission?: boolean;
}

// Microsoft Teams Integration
export interface TeamsIntegrationConfig extends BaseIntegrationConfig {
  provider: 'teams';
  webhookUrl: string;            // Incoming webhook URL
  messageTemplate?: string;
  adaptiveCard?: boolean;        // Use Adaptive Card format
}

// Discord Integration
export interface DiscordIntegrationConfig extends BaseIntegrationConfig {
  provider: 'discord';
  webhookUrl: string;
  embedTitle?: string;
  embedColor?: string;           // Hex color for embed
}

// Mailchimp Integration
export interface MailchimpIntegrationConfig extends BaseIntegrationConfig {
  provider: 'mailchimp';
  audienceId: string;            // List ID
  apiKey?: string;               // Encrypted
  dataCenter: string;            // e.g., us1, us2
  doubleOptIn?: boolean;
  tags?: string[];
  updateExisting?: boolean;      // Update if email exists
}

// ActiveCampaign Integration
export interface ActiveCampaignIntegrationConfig extends BaseIntegrationConfig {
  provider: 'activecampaign';
  accountUrl: string;            // e.g., yourcompany.activehosted.com
  apiKey?: string;               // Encrypted
  listId?: string;
  tags?: string[];
  automationId?: string;         // Trigger an automation
}

// Wix Integration (for embedding in Wix sites)
export interface WixIntegrationConfig extends BaseIntegrationConfig {
  provider: 'wix';
  siteId?: string;
  embedMode: 'iframe' | 'widget';
  syncToContacts?: boolean;      // Sync submissions to Wix Contacts
  wixFormsId?: string;           // Link to Wix Forms
}

// ============================================================================
// OFFICE 365 / MICROSOFT 365 INTEGRATIONS
// ============================================================================

// Excel Online Integration (OneDrive or SharePoint)
export interface ExcelOnlineIntegrationConfig extends BaseIntegrationConfig {
  provider: 'excel_online';
  workbookId: string;            // ID of the Excel workbook
  worksheetName?: string;        // Sheet name (defaults to first)
  tableId?: string;              // If using Excel table within sheet
  driveId?: string;              // OneDrive/SharePoint drive ID
  siteId?: string;               // SharePoint site ID (if SharePoint)
  headerRow?: number;            // Row containing headers
  appendToEnd?: boolean;
  accessToken?: string;          // Encrypted
  refreshToken?: string;         // Encrypted
  tokenExpiresAt?: string;
}

// OneDrive Integration (save files/data)
export interface OneDriveIntegrationConfig extends BaseIntegrationConfig {
  provider: 'onedrive';
  folderId?: string;             // Destination folder ID
  folderPath?: string;           // Or folder path like /Forms/Submissions
  fileNameTemplate?: string;     // e.g., "{form_name}_{submission_id}.json"
  fileFormat: 'json' | 'csv' | 'pdf';
  includeAttachments?: boolean;  // Upload file attachments too
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// SharePoint Integration
export interface SharePointIntegrationConfig extends BaseIntegrationConfig {
  provider: 'sharepoint';
  siteId: string;                // SharePoint site ID
  targetType: 'list' | 'library';
  listId?: string;               // SharePoint list ID (if list)
  libraryId?: string;            // Document library ID (if library)
  folderId?: string;             // Folder within library
  contentTypeId?: string;        // SharePoint content type
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// Outlook Integration (calendar events, emails)
export interface OutlookIntegrationConfig extends BaseIntegrationConfig {
  provider: 'outlook';
  actionType: 'create_event' | 'send_email' | 'create_contact';
  // For events
  calendarId?: string;           // Calendar ID (default: primary)
  eventTitleField?: string;      // Form field to use as event title
  eventDateField?: string;       // Form field for event date
  eventDuration?: number;        // Duration in minutes
  eventReminder?: number;        // Reminder minutes before
  // For emails
  toEmailField?: string;         // Form field containing recipient
  emailSubjectTemplate?: string;
  emailBodyTemplate?: string;
  sendAsNotification?: boolean;  // Send notification to form owner
  // For contacts
  contactFolderId?: string;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// OneNote Integration
export interface OneNoteIntegrationConfig extends BaseIntegrationConfig {
  provider: 'onenote';
  notebookId: string;
  sectionId: string;
  pageTitleTemplate?: string;    // e.g., "Form Submission - {date}"
  includeAllFields?: boolean;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// Microsoft To-Do Integration
export interface MicrosoftTodoIntegrationConfig extends BaseIntegrationConfig {
  provider: 'microsoft_todo';
  listId?: string;               // Task list ID (default: default list)
  taskTitleField?: string;       // Form field for task title
  taskNotesTemplate?: string;    // Template for task notes
  dueDateField?: string;         // Form field for due date
  priority?: 'low' | 'normal' | 'high';
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// Microsoft Planner Integration
export interface PlannerIntegrationConfig extends BaseIntegrationConfig {
  provider: 'planner';
  planId: string;
  bucketId?: string;             // Bucket to add tasks to
  taskTitleField?: string;
  taskNotesTemplate?: string;
  assignToField?: string;        // Form field with assignee email
  dueDateField?: string;
  priority?: number;             // 1-10 (lower = higher priority)
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
}

// Union type for all integration configs
export type IntegrationConfig =
  | WebhookIntegrationConfig
  | SalesforceIntegrationConfig
  | HubSpotIntegrationConfig
  | MondayIntegrationConfig
  | PipedriveIntegrationConfig
  | ZohoCrmIntegrationConfig
  | Dynamics365IntegrationConfig
  | GoogleSheetsIntegrationConfig
  | AirtableIntegrationConfig
  | NotionIntegrationConfig
  | SlackIntegrationConfig
  | TeamsIntegrationConfig
  | DiscordIntegrationConfig
  | MailchimpIntegrationConfig
  | ActiveCampaignIntegrationConfig
  | WixIntegrationConfig
  // Office 365
  | ExcelOnlineIntegrationConfig
  | OneDriveIntegrationConfig
  | SharePointIntegrationConfig
  | OutlookIntegrationConfig
  | OneNoteIntegrationConfig
  | MicrosoftTodoIntegrationConfig
  | PlannerIntegrationConfig;

// Integration execution log
export interface IntegrationLog {
  id: string;
  integrationId: string;
  formId: string;
  submissionId: string;
  status: 'success' | 'failed' | 'pending' | 'retrying';
  provider: IntegrationProvider;
  requestPayload?: string;       // JSON string
  responsePayload?: string;      // JSON string
  errorMessage?: string;
  executedAt: string;
  durationMs?: number;
  retryCount?: number;
}

// Form Access Control - Password protection methods
export type AccessControlMethod = 'password' | 'multi_password' | 'user_password' | 'email_otp';

export interface UserCredential {
  username: string;
  passwordHash: string;  // Will be hashed on backend
}

export interface FormAccessControl {
  enabled: boolean;
  method: AccessControlMethod;

  // For 'password' method - single password
  password?: string;  // Will be hashed on backend

  // For 'multi_password' method - multiple accepted passwords
  passwords?: string[];  // Will be hashed on backend

  // For 'user_password' method - username + password pairs
  credentials?: UserCredential[];

  // For 'email_otp' method - allowed email addresses
  allowedEmails?: string[];
  otpExpiryMinutes?: number;  // How long OTP is valid (default: 10)

  // UI customization
  accessMessage?: string;  // Custom message shown on access gate
  accessTitle?: string;    // Custom title for access gate
}

// Results Access Control - Password protection for public results viewing
export type ResultsAccessMethod = 'password' | 'user_password' | 'email_otp';

export interface ResultsAccessControl {
  enabled: boolean;
  method: ResultsAccessMethod;

  // For 'password' method - single password
  password?: string;

  // For 'user_password' method - username + password pairs
  credentials?: UserCredential[];

  // For 'email_otp' method - allowed email addresses
  allowedEmails?: string[];
  otpExpiryMinutes?: number;  // How long OTP is valid (default: 10)

  // UI customization
  accessMessage?: string;  // Custom message shown on access gate
  accessTitle?: string;    // Custom title for access gate
}

// Form Settings
export interface FormSettings {
  theme?: ThemeSetting;
  direction?: 'ltr' | 'rtl';  // Text direction for the form
  subtitle?: string;
  instructions?: string;
  submitButtonText?: string;
  successMessage?: string;
  redirectUrl?: string;
  submitActions?: SubmitAction[];
  autoResponder?: AutoResponderSettings;
  saveAndResume?: SaveAndResumeSettings;
  quiz?: QuizSettings;
  enableCaptcha?: boolean;

  // Branding
  logo?: string;      // URL or base64 data URL for form logo
  favicon?: string;   // URL or base64 data URL for form favicon

  // Security settings
  encryptSubmissions?: boolean;  // Enable AES-256 encryption for submission data

  // Access Control - Password protection for forms
  accessControl?: FormAccessControl;

  // Results Access Control - Password protection for public results viewing
  resultsAccessControl?: ResultsAccessControl;

  // Multilingual settings
  multilingual?: FormLanguageSettings;
  subtitles?: MultilingualText;
  instructionsTexts?: MultilingualText;
  submitButtonTexts?: MultilingualText;
  successMessages?: MultilingualText;

  // Product & Order settings
  product?: ProductSettings;
  order?: OrderSettings;
  portal?: PortalSettings;
}

// Complete Form Schema
export interface FormSchema {
  version: number;
  fields: FormField[];
  steps?: FormStep[];
  settings: FormSettings;
  conditionalLogic: ConditionalRule[];
}

// Form type determines URL prefix: /f/ (form), /p/ (poll), /q/ (quiz), /l/ (live_quiz), /product/ (product), /order/ (order)
export type FormType = 'form' | 'poll' | 'quiz' | 'live_quiz' | 'product' | 'order';

// URL prefix mapping for form types
export const FORM_TYPE_URL_PREFIX: Record<FormType, string> = {
  form: '/f/',
  poll: '/p/',
  quiz: '/q/',
  live_quiz: '/l/',
  product: '/product/',
  order: '/order/',
};

// Product-specific settings
export interface ProductSettings {
  enabled: boolean;
  // Product info
  price?: number;
  currency?: string;
  compareAtPrice?: number; // Original/strikethrough price
  sku?: string;
  barcode?: string;
  // Inventory
  trackInventory?: boolean;
  inventoryQuantity?: number;
  allowBackorders?: boolean;
  lowStockThreshold?: number;
  // Variants
  hasVariants?: boolean;
  variants?: ProductVariant[];
  // Images
  images?: ProductImage[];
  featuredImageIndex?: number;
  // Categories & Tags
  category?: string;
  tags?: string[];
  // Shipping
  weight?: number;
  weightUnit?: 'kg' | 'lb' | 'oz' | 'g';
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
    unit?: 'cm' | 'in';
  };
  requiresShipping?: boolean;
  // SEO
  seoTitle?: string;
  seoDescription?: string;
  // Status
  isActive?: boolean;
  publishedAt?: string;
  // Custom Domain
  customDomain?: string;
  cnameVerified?: boolean;
}

export interface ProductVariant {
  id: string;
  name: string;
  sku?: string;
  price: number;
  compareAtPrice?: number;
  inventoryQuantity?: number;
  options: Record<string, string>; // e.g., { color: 'red', size: 'M' }
  imageIndex?: number;
}

export interface ProductImage {
  id: string;
  url: string;
  alt?: string;
  position: number;
}

// Order-specific settings
export interface OrderSettings {
  enabled: boolean;
  // Order configuration
  orderNumberPrefix?: string;
  orderNumberStart?: number;
  // Payment
  requirePayment?: boolean;
  paymentMethods?: ('stripe' | 'paypal' | 'cod' | 'bank_transfer')[];
  // Shipping
  shippingMethods?: ShippingMethod[];
  collectShippingAddress?: boolean;
  collectBillingAddress?: boolean;
  // Tax
  collectTax?: boolean;
  taxRate?: number;
  taxIncluded?: boolean;
  // Customer
  requireAccount?: boolean;
  guestCheckout?: boolean;
  // Notifications
  sendOrderConfirmation?: boolean;
  sendShippingNotification?: boolean;
  notificationEmail?: string;
  // Custom Domain
  customDomain?: string;
  cnameVerified?: boolean;
}

export interface ShippingMethod {
  id: string;
  name: string;
  description?: string;
  price: number;
  estimatedDays?: string;
  isDefault?: boolean;
}

// Portal settings for online store
export interface PortalSettings {
  enabled: boolean;
  name: string;
  description?: string;
  logo?: string;
  favicon?: string;
  // Branding
  theme: PortalTheme;
  // Custom Domain
  customDomain?: string;
  subdomain?: string;
  cnameVerified?: boolean;
  // Layout
  layout: 'grid' | 'list' | 'masonry';
  productsPerPage?: number;
  showCategories?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  // Navigation
  navigationItems?: PortalNavigationItem[];
  // Social
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
  // SEO
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  // Analytics
  googleAnalyticsId?: string;
  facebookPixelId?: string;
  // Legal
  termsUrl?: string;
  privacyUrl?: string;
  refundPolicyUrl?: string;
  // Contact
  contactEmail?: string;
  contactPhone?: string;
  contactAddress?: string;
}

export interface PortalTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  accentColor: string;
  fontFamily?: string;
  headerStyle?: 'minimal' | 'standard' | 'centered';
  footerStyle?: 'minimal' | 'standard' | 'detailed';
  productCardStyle?: 'minimal' | 'detailed' | 'overlay';
  borderRadius?: 'none' | 'small' | 'medium' | 'large';
}

export interface PortalNavigationItem {
  id: string;
  label: string;
  url: string;
  isExternal?: boolean;
  position: number;
}

// Get the public URL for a form
export function getFormPublicUrl(slug: string, formType: FormType = 'form'): string {
  return `${FORM_TYPE_URL_PREFIX[formType]}${slug}`;
}

// Database Form Record
export interface Form {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  schema: FormSchema;
  settings: FormSettings;
  status: 'draft' | 'published' | 'archived';
  form_type: FormType;
  version: number;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

// Form List Item (for admin dashboard)
export interface FormListItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: 'draft' | 'published' | 'archived';
  form_type: FormType;
  version: number;
  created_at: string;
  updated_at: string;
}
